import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

export const formatDate = (date: string | Date | unknown, formatStr: string = 'dd/MM/yyyy'): string => {
  try {
    if (!date) return '';
    if (typeof date !== 'string' && !(date instanceof Date)) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    return format(dateObj, formatStr, { locale: es });
  } catch {
    return 'Fecha inválida';
  }
};

/**
 * Formatea una fecha con formato completo: día, mes largo, año, hora y minuto
 * Ejemplo: "15 de enero de 2024, 14:30"
 */
export const formatDateFull = (date: string | Date | unknown): string => {
  try {
    if (!date) return '';
    if (typeof date !== 'string' && !(date instanceof Date)) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    return dateObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Fecha inválida';
  }
};

/**
 * Formatea una fecha corta sin hora: día, mes corto, año
 * Ejemplo: "15 ene 2024"
 */
export const formatDateShort = (date: string | Date | unknown): string => {
  try {
    if (!date) return '';
    if (typeof date !== 'string' && !(date instanceof Date)) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    return dateObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return 'Fecha inválida';
  }
};

/**
 * Formatea una fecha corta con hora: día, mes corto, año, hora y minuto
 * Ejemplo: "15 ene 2024, 14:30"
 */
export const formatDateShortWithTime = (date: string | Date | unknown): string => {
  try {
    if (!date) return '';
    if (typeof date !== 'string' && !(date instanceof Date)) return '';
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'Fecha inválida';
    
    return dateObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Fecha inválida';
  }
};

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Convierte un texto en snake_case o kebab-case a Title Case
 * Ejemplo: "client_meeting" -> "Client Meeting"
 * Ejemplo: "exercise_completed" -> "Exercise Completed"
 */
export const formatSnakeCaseToTitle = (text: string): string => {
  if (!text) return '';
  
  return text
    .split(/[_\s-]+/)
    .map(word => {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

/**
 * Formatea el tipo de simulación para mostrarlo correctamente
 * Maneja casos especiales como "client_meeting" -> "Client Meeting"
 */
export const formatSimulationType = (type: string): string => {
  if (!type) return '';
  
  // Mapeo de tipos conocidos
  const typeMap: Record<string, string> = {
    'court': 'Court',
    'negotiation': 'Negotiation',
    'client_meeting': 'Client Meeting',
  };
  
  // Si está en el mapa, usar el valor mapeado
  if (typeMap[type]) {
    return typeMap[type];
  }
  
  // Si no, formatear usando snake_case
  return formatSnakeCaseToTitle(type);
};

/**
 * Formatea una clave de requisito de badge para mostrarla correctamente
 * Ejemplo: "client_meeting_simulations" -> "Client Meeting Simulations"
 */
export const formatBadgeRequirementKey = (key: string): string => {
  if (!key) return '';
  
  // Mapeo de claves conocidas
  const keyMap: Record<string, string> = {
    'exercises_completed': 'Exercises Completed',
    'exams_taken': 'Exams Taken',
    'simulations_completed': 'Simulations Completed',
    'client_meeting_simulations': 'Client Meeting Simulations',
    'court_simulations': 'Court Simulations',
    'negotiation_simulations': 'Negotiation Simulations',
    'average_score': 'Average Score',
    'badges_earned': 'Badges Earned',
  };
  
  // Si está en el mapa, usar el valor mapeado
  if (keyMap[key]) {
    return keyMap[key];
  }
  
  // Si no, formatear usando snake_case
  return formatSnakeCaseToTitle(key);
};

